import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Tooltip from '@radix-ui/react-tooltip';
import { useTotalUsers } from 'src/common/hooks/use-total-users';
import { formatToNearest500K } from 'src/common/utils/formatting';
import GoogleLogo from 'src/images/pngs/soc-sim/google.png';
import HuntressLogo from 'src/images/pngs/soc-sim/huntress-logo-white.png';
import KpmgLogo from 'src/images/pngs/soc-sim/kpmg.png';
import NetworkIcon from 'src/images/pngs/soc-sim/network.png';
import SSandCLogo from 'src/images/pngs/soc-sim/ssc-logo-white.png';
import TrustIndicatorsImage from 'src/images/svgs/business/trust-indicators.svg';
import ThreatlockerLogo from 'src/images/svgs/soc-sim/threatlocker.svg';
import { StyledCarouselContainer, StyledCarouselTrack, StyledCaseStudyBadge, StyledCaseStudyTooltipContent, StyledCompaniesWrapper, StyledCompanyImage, StyledCompanyLogoCaseStudyWrapper, StyledContainer, StyledHeading, StyledIndustryTag, StyledRatingBadgesContainer, StyledTestimonialAuthor, StyledTestimonialName, StyledTestimonialQuote, StyledTooltipHeader } from './trust-indicators.styles';
const COMPANY_LOGOS: {
  src: string;
  alt: string;
  hasCaseStudy: boolean;
  industry?: string;
  quote?: string;
  url?: string;
  name?: string;
  author?: string;
}[] = [{
  src: GoogleLogo,
  alt: 'Google',
  hasCaseStudy: true,
  quote: `I'm personally a massive fan of TryHackMe - I've been using the platform since it first came out! I only wish I had more time to complete more rooms. I use TryHackMe for personal development and thoroughly enjoy incorporating it into my work responsibilities. I was incredibly excited to follow the opportunity for a customer-facing CTF event, and the results have spoken for themselves!`,
  url: 'https://assets.tryhackme.com/business/Google%2BCase%2BStudy.pdf',
  name: 'Keith Manville',
  author: 'Google Security',
  industry: 'Technology'
}, {
  src: KpmgLogo,
  alt: 'KPMG',
  hasCaseStudy: true,
  quote: `TryHackMe labs have a brilliant balance of theory and practice - explaining how cyber tools work and showcasing them in interactive real-world environments. It's a fun, straightforward platform with clear guidance, so it's easy to upskill independently. Bite-sized content is especially useful for training in between other priorities.`,
  url: 'https://assets.tryhackme.com/business/KPMG%2BCase%2BStudy.pdf',
  industry: 'Technology'
}, {
  src: SSandCLogo,
  alt: 'SS&C',
  hasCaseStudy: false
}, {
  src: NetworkIcon,
  alt: 'Network Intelligence',
  hasCaseStudy: false
}, {
  src: HuntressLogo,
  alt: 'Huntress',
  hasCaseStudy: false
}, {
  src: ThreatlockerLogo,
  alt: 'ThreatLocker',
  hasCaseStudy: false
}] as const;
const CaseStudyTooltip = ({
  company
}: {
  company: (typeof COMPANY_LOGOS)[number];
}) => <StyledCaseStudyTooltipContent sideOffset={20} alignOffset={100} data-sentry-element="StyledCaseStudyTooltipContent" data-sentry-component="CaseStudyTooltip" data-sentry-source-file="trust-indicators.tsx">
    <StyledTooltipHeader data-sentry-element="StyledTooltipHeader" data-sentry-source-file="trust-indicators.tsx">
      <img src={company.src} alt={company.alt} />
      {company.industry && <StyledIndustryTag>{company.industry}</StyledIndustryTag>}
    </StyledTooltipHeader>
    <div>
      {company.quote && <StyledTestimonialQuote>{company.quote}</StyledTestimonialQuote>}
      <div>
        {company.name && <StyledTestimonialName>{company.name}</StyledTestimonialName>}
        {company.author && <StyledTestimonialAuthor>{company.author}</StyledTestimonialAuthor>}
      </div>
    </div>
  </StyledCaseStudyTooltipContent>;
const RenderCompanyLogo = ({
  company,
  keyPrefix
}: {
  company: (typeof COMPANY_LOGOS)[number];
  keyPrefix: string;
}) => {
  const handleCaseStudyClick = () => {
    window.open(company?.url, '_blank', 'noopener,noreferrer');
  };
  if (company.hasCaseStudy) {
    return <div key={`${keyPrefix}-${company.alt}`}>
        <StyledCompanyImage src={company.src} alt={company.alt} />
        <Tooltip.Provider delayDuration={200} disableHoverableContent>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <StyledCompanyLogoCaseStudyWrapper onClick={handleCaseStudyClick}>
                <StyledCaseStudyBadge>
                  Case study <FontAwesomeIcon size="xs" icon={faChevronRight} />
                </StyledCaseStudyBadge>
              </StyledCompanyLogoCaseStudyWrapper>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <CaseStudyTooltip company={company} />
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      </div>;
  }
  return <StyledCompanyImage key={`${keyPrefix}-${company.alt}`} src={company.src} alt={company.alt} data-sentry-element="StyledCompanyImage" data-sentry-component="RenderCompanyLogo" data-sentry-source-file="trust-indicators.tsx" />;
};
export const TrustIndicators = () => {
  const {
    totalUsers
  } = useTotalUsers();
  const formattedTotalUsers = formatToNearest500K(totalUsers);
  return <StyledContainer data-sentry-element="StyledContainer" data-sentry-component="TrustIndicators" data-sentry-source-file="trust-indicators.tsx">
      <StyledHeading data-testid="trust-indicators-heading" data-sentry-element="StyledHeading" data-sentry-source-file="trust-indicators.tsx">
        Trusted by more than 1000+ security teams and {formattedTotalUsers}+ million users
      </StyledHeading>

      <StyledRatingBadgesContainer data-sentry-element="StyledRatingBadgesContainer" data-sentry-source-file="trust-indicators.tsx">
        <img src={TrustIndicatorsImage} alt="Trust Indicators" />
      </StyledRatingBadgesContainer>

      <StyledCompaniesWrapper data-sentry-element="StyledCompaniesWrapper" data-sentry-source-file="trust-indicators.tsx">
        <StyledCarouselContainer data-sentry-element="StyledCarouselContainer" data-sentry-source-file="trust-indicators.tsx">
          <StyledCarouselTrack data-sentry-element="StyledCarouselTrack" data-sentry-source-file="trust-indicators.tsx">
            {/* First set of logos */}
            {COMPANY_LOGOS.map(company => RenderCompanyLogo({
            company,
            keyPrefix: 'logo-1'
          }))}
            {/* Duplicate set for infinite scroll */}
            {COMPANY_LOGOS.map(company => RenderCompanyLogo({
            company,
            keyPrefix: 'logo-2'
          }))}
          </StyledCarouselTrack>
        </StyledCarouselContainer>
      </StyledCompaniesWrapper>
    </StyledContainer>;
};